import {
  type CSSProperties,
  type KeyboardEvent,
  useRef,
  useState,
} from "react";
import { MicroLabel } from "@/components/ui/MicroLabel";
import { Panel } from "@/components/ui/Panel";
import type {
  FigureListResponse,
  FigurePagePreferenceModel,
  FigureResponse,
} from "@patent-studio/api-types";
import { Button } from "@/components/ui/button";
import { placedFileIds } from "@/lib/figure-labels";
import {
  type Bounds,
  type Sheet,
  DEFAULT_PAGE_PREFERENCE,
  SHEET,
  USABLE,
  drawingBox,
  packSheets,
  standsAlone,
} from "@/lib/sheet-packing";
import { useFigureDrawing } from "@/hooks/use-figure-drawing";
import { LoadFailure } from "@/components/shared/LoadFailure";
import { useFigureLayout } from "@/hooks/use-figure-layout";
import { useFigureReview } from "@/hooks/use-figure-review";
import { useDismissed } from "@/components/ui/dismiss";
import { ReviewSection } from "./ReviewSection";

/**
 * The final layout preview: the drawing sheets as the filed draft will carry
 * them, with the page decisions made right on the preview.
 *
 * Selecting a figure opens its options: orientation, and own page. Landscape
 * locks own page on — the implication is the packing projection's rule, so the
 * popover only reflects it. Every change writes through the layout PUT, which
 * repacks the sheets at once and lapses the arrangement's confirmation through
 * the version anchor, never through UI bookkeeping.
 */

/**
 * A length on the sheet as a share of the usable area it lies along. The usable
 * area is the container, so the sheet keeps the PDF's proportions at any width.
 */
const alongHeight = (cm: number) => `${String((cm / USABLE.height) * 100)}cqh`;
const alongWidth = (cm: number) => `${String((cm / USABLE.width) * 100)}cqw`;

/** The sheet's margins as shares of the page: top, right, bottom, left. */
const SHEET_INSET = [
  SHEET.margin.top / SHEET.page.height,
  SHEET.margin.right / SHEET.page.width,
  SHEET.margin.bottom / SHEET.page.height,
  SHEET.margin.left / SHEET.page.width,
]
  .map((share) => `${String(share * 100)}%`)
  .join(" ");

/**
 * The caption lines under each figure, as the Drawings PDF sets them: "FIG. n",
 * then "Prior Art" on a figure the attorney marked so. A sole figure in the
 * matter carries no label, so a marked one carries the legend alone.
 */
function captionLines(
  byId: ReadonlyMap<string, FigureResponse>,
  soleFigure: boolean,
  priorArt: ReadonlySet<string>,
): (fileId: string) => string[] {
  return (fileId) => [
    ...(soleFigure ? [] : [`FIG. ${byId.get(fileId)?.label ?? ""}`]),
    ...(priorArt.has(fileId) ? ["Prior Art"] : []),
  ];
}

function boundsOf(landscape: boolean, sheet: Sheet): Bounds {
  if (landscape) return "whole-sheet-turned";
  if (sheet.columns === 2) return "quarter-sheet";
  return sheet.figures.length === 1 ? "whole-sheet" : "half-sheet";
}

/**
 * The drawing at its aspect ratio, as wide as its room and no higher: it scales
 * up as well as down, as the PDF sets it, and its label sits right under it.
 * A turned room's height runs across the sheet, so it is a share of the width.
 */
function drawingStyle(bounds: Bounds, lines: number): CSSProperties {
  const room = drawingBox(bounds, lines);
  const turned = bounds === "whole-sheet-turned";
  return {
    width: "100%",
    height: "auto",
    maxHeight: turned ? alongWidth(room.height) : alongHeight(room.height),
  };
}

/**
 * The frame the drawing and its label are set in, centred together. A turned
 * frame is the area under the number band turned a quarter, as the PDF turns
 * it: its width runs up the sheet and its top lies along the left margin.
 */
function frameStyle(bounds: Bounds): CSSProperties | undefined {
  if (bounds !== "whole-sheet-turned") return undefined;
  return {
    position: "absolute",
    top: "50%",
    left: "50%",
    width: alongHeight(USABLE.height - SHEET.band.number),
    height: "100cqw",
    translate: "-50% -50%",
    rotate: "-90deg",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  };
}

function SheetDrawing({
  taskId,
  figure,
  bounds,
  lines,
}: {
  taskId: string;
  figure: FigureResponse;
  bounds: Bounds;
  /** How many caption lines sit under the drawing, each a band of the sheet. */
  lines: number;
}) {
  const { url, error, retry } = useFigureDrawing(taskId, figure.file_id);
  if (url === undefined && error !== null) {
    return (
      <LoadFailure
        error={error}
        fallback="The drawing could not be prepared."
        onRetry={retry}
      />
    );
  }
  if (url === undefined) {
    return (
      <div className="grid h-24 place-items-center text-[10px] text-muted-foreground">
        Preparing…
      </div>
    );
  }
  return (
    <img
      src={url}
      alt={`Drawing of figure ${figure.label ?? ""}`}
      data-bounds={bounds}
      data-lines={lines}
      className="object-contain"
      style={drawingStyle(bounds, lines)}
    />
  );
}

/** Which way up the figure sits. Landscape takes a page to itself. */
function Orientation({
  landscape,
  ownPage,
  onChange,
}: {
  landscape: boolean;
  ownPage: boolean;
  onChange: (preference: FigurePagePreferenceModel) => void;
}) {
  return (
    <>
      <MicroLabel as="p" size="sm" className="mb-1">
        Orientation
      </MicroLabel>
      <div className="mb-2 flex flex-col gap-1">
        <Button
          size="dense"
          variant={landscape ? "secondary" : "primary"}
          onClick={() => {
            onChange({ orientation: "portrait", own_page: ownPage });
          }}
        >
          Portrait
        </Button>
        <Button
          size="dense"
          variant={landscape ? "primary" : "secondary"}
          onClick={() => {
            onChange({ orientation: "landscape", own_page: false });
          }}
        >
          Landscape
        </Button>
      </div>
    </>
  );
}

/** The selected figure's page options, beside its sheet. */
function PageOptions({
  figure,
  preference,
  panelRef,
  onChange,
  onClose,
}: {
  figure: FigureResponse;
  preference: FigurePagePreferenceModel;
  panelRef: React.RefObject<HTMLDivElement | null>;
  onChange: (preference: FigurePagePreferenceModel) => void;
  onClose: () => void;
}) {
  const landscape = preference.orientation === "landscape";
  const alone = standsAlone(preference);
  return (
    <Panel
      pad="tight"
      ref={panelRef}
      data-testid="layout-options"
      // The popover sits inside the figure whose click toggles the selection;
      // a click in here is aimed at the options, so it stops at this edge or
      // the close control would reopen what it just closed.
      onClick={(event) => event.stopPropagation()}
      className="absolute left-full top-0 z-10 ml-2 w-40 text-left shadow-lg"
    >
      <div className="mb-2 flex items-center gap-2">
        <span className="font-mono text-xs font-semibold">
          FIG. {figure.label}
        </span>
        <Button
          size="dense"
          variant="quiet"
          className="ml-auto h-6 px-2"
          aria-label="Close page options"
          onClick={onClose}
        >
          ✕
        </Button>
      </div>
      <Orientation
        landscape={landscape}
        ownPage={preference.own_page}
        onChange={onChange}
      />
      <Button
        size="dense"
        variant={alone ? "primary" : "secondary"}
        disabled={landscape}
        className="w-full"
        onClick={() =>
          onChange({
            orientation: preference.orientation,
            own_page: !preference.own_page,
          })
        }
      >
        {alone ? "✓ Own page" : "Own page"}
      </Button>
      <p className="mt-2 text-[10px] text-muted-foreground">
        {landscape
          ? "Landscape figures are rotated on their own page."
          : "Two figures share a sheet unless given their own page."}
      </p>
    </Panel>
  );
}

/**
 * The caption under the drawing, one band a line: its label, then "Prior Art"
 * on a figure the attorney marked so. A turned frame carries it turned with
 * the drawing.
 */
function SheetCaption({ lines, turned }: { lines: string[]; turned: boolean }) {
  if (lines.length === 0) return null;
  const band = SHEET.band.caption;
  const height = turned ? alongWidth(band) : alongHeight(band);
  return (
    <figcaption className="font-serif text-[13px] font-semibold text-black">
      {lines.map((line) => (
        <span
          key={line}
          className="flex items-center justify-center"
          style={{ height }}
        >
          {line}
        </span>
      ))}
    </figcaption>
  );
}

/**
 * The keys a button answers, for the figure: the options popover nests its own
 * buttons, so the figure cannot be a button itself. A key from inside it is not
 * the figure's.
 */
function pressedLikeAButton(onPress: () => void) {
  return (event: KeyboardEvent<HTMLElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    onPress();
  };
}

/** One figure on a sheet: the drawing, its caption, and its page options. */
function SheetFigure({
  sheetFigure,
  taskId,
  byId,
  captionOf,
  bounds,
  selected,
  preferenceOf,
  onToggleSelected,
  onSetPreference,
  onClosePageOptions,
}: {
  sheetFigure: ReturnType<typeof packSheets>[number]["figures"][number];
  taskId: string;
  byId: Map<string, FigureResponse>;
  captionOf: (fileId: string) => string[];
  bounds: Bounds;
  selected: string | undefined;
  preferenceOf: (fileId: string) => FigurePagePreferenceModel;
  onToggleSelected: (fileId: string) => void;
  onSetPreference: (
    fileId: string,
    preference: FigurePagePreferenceModel,
  ) => void;
  onClosePageOptions: () => void;
}) {
  const shown = byId.get(sheetFigure.fileId);
  const panelRef = useRef<HTMLDivElement>(null);
  const figureRef = useRef<HTMLElement>(null);
  // The same dismissal every other popover on the figures screen has: Escape as
  // the nearest scope, and a press or a focus reaching anything behind it.
  useDismissed(
    selected === sheetFigure.fileId,
    panelRef,
    figureRef,
    onClosePageOptions,
  );
  if (shown === undefined) return null;
  const caption = captionOf(sheetFigure.fileId);
  return (
    <figure
      ref={figureRef}
      key={sheetFigure.fileId}
      data-testid={`layout-figure-${sheetFigure.fileId}`}
      data-landscape={sheetFigure.landscape}
      className="relative m-0 flex min-h-0 flex-1 cursor-pointer flex-col justify-center text-center"
      title="Click to set this figure's page layout"
      role="button"
      aria-label={`Set the page layout of FIG. ${shown.label}`}
      tabIndex={0}
      onClick={() => onToggleSelected(sheetFigure.fileId)}
      onKeyDown={pressedLikeAButton(() => onToggleSelected(sheetFigure.fileId))}
    >
      <div style={frameStyle(bounds)}>
        <SheetDrawing
          taskId={taskId}
          figure={shown}
          bounds={bounds}
          lines={caption.length}
        />
        <SheetCaption lines={caption} turned={sheetFigure.landscape} />
      </div>
      {selected === sheetFigure.fileId && (
        <PageOptions
          figure={shown}
          panelRef={panelRef}
          preference={preferenceOf(sheetFigure.fileId)}
          onChange={(preference) => {
            onSetPreference(sheetFigure.fileId, preference);
          }}
          onClose={onClosePageOptions}
        />
      )}
    </figure>
  );
}

/** One drawing sheet as it will be filed, with its figures on it. */
function LayoutSheet({
  sheet,
  sheetIndex,
  sheetCount,
  taskId,
  byId,
  captionOf,
  selected,
  preferenceOf,
  onToggleSelected,
  onSetPreference,
  onClosePageOptions,
}: {
  sheet: ReturnType<typeof packSheets>[number];
  sheetIndex: number;
  sheetCount: number;
  taskId: string;
  byId: Map<string, FigureResponse>;
  captionOf: (fileId: string) => string[];
  selected: string | undefined;
  preferenceOf: (fileId: string) => FigurePagePreferenceModel;
  onToggleSelected: (fileId: string) => void;
  onSetPreference: (
    fileId: string,
    preference: FigurePagePreferenceModel,
  ) => void;
  onClosePageOptions: () => void;
}) {
  return (
    <div
      key={sheet.figures.map((f) => f.fileId).join("+")}
      data-testid={`layout-sheet-${sheetIndex + 1}`}
      className="relative w-[340px] max-w-full border border-border bg-white shadow-sm"
      style={{
        aspectRatio: `${String(SHEET.page.width)}/${String(SHEET.page.height)}`,
      }}
    >
      <div
        className="absolute flex flex-col [container-type:size]"
        style={{ inset: SHEET_INSET }}
      >
        <p
          className="flex flex-none items-center justify-center font-serif text-xs text-black"
          style={{ height: alongHeight(SHEET.band.number) }}
        >
          {sheetIndex + 1}/{sheetCount}
        </p>
        {/* A group's three or four views sit two across, as the PDF sets them. */}
        <div
          className={
            sheet.columns === 2
              ? "grid min-h-0 flex-1 grid-cols-2 grid-rows-2"
              : "flex min-h-0 flex-1 flex-col"
          }
          style={{
            rowGap: alongHeight(SHEET.band.gap),
            columnGap: alongWidth(SHEET.band.column),
          }}
        >
          {sheet.figures.map((sheetFigure) => (
            <SheetFigure
              key={sheetFigure.fileId}
              sheetFigure={sheetFigure}
              taskId={taskId}
              byId={byId}
              captionOf={captionOf}
              bounds={boundsOf(sheetFigure.landscape, sheet)}
              selected={selected}
              preferenceOf={preferenceOf}
              onToggleSelected={onToggleSelected}
              onSetPreference={onSetPreference}
              onClosePageOptions={onClosePageOptions}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export function FigureLayoutPreview({
  taskId,
  data,
}: {
  taskId: string;
  data: FigureListResponse;
}) {
  const layout = useFigureLayout(taskId);
  const review = useFigureReview(taskId);
  const [selected, setSelected] = useState<string | undefined>(undefined);
  const toggleSelected = (fileId: string) =>
    setSelected((current) => (current === fileId ? undefined : fileId));

  const byId = new Map((data.figures ?? []).map((f) => [f.file_id, f]));
  const order = placedFileIds(data.layout?.entries ?? []);
  const preferences = data.layout?.page_preferences ?? {};
  const sheets = packSheets(data.layout?.entries ?? [], preferences);
  const captionOf = captionLines(
    byId,
    order.length === 1,
    new Set(
      (data.definitions?.figures ?? [])
        .filter((definition) => definition.prior_art)
        .map((definition) => definition.file_id),
    ),
  );
  const reviewed = data.review?.layout_confirmed === true;

  const preferenceOf = (fileId: string): FigurePagePreferenceModel =>
    preferences[fileId] ?? DEFAULT_PAGE_PREFERENCE;

  return (
    <ReviewSection
      title="Final layout preview"
      reviewed={reviewed}
      tagTestId="layout-tag"
    >
      <p className="mt-2 text-xs text-muted-foreground">
        How the drawing sheets will appear in the filed draft. Select any figure
        to give it its own page or turn it landscape; every change repacks and
        renumbers the sheets at once.
      </p>
      <div className="mt-3 flex flex-col items-center gap-4 rounded-lg bg-muted/40 p-4">
        {sheets.map((sheet, sheetIndex) => (
          <LayoutSheet
            key={sheet.figures.map((f) => f.fileId).join("+")}
            sheet={sheet}
            sheetIndex={sheetIndex}
            sheetCount={sheets.length}
            taskId={taskId}
            byId={byId}
            captionOf={captionOf}
            selected={selected}
            preferenceOf={preferenceOf}
            onToggleSelected={toggleSelected}
            onSetPreference={layout.setPagePreference}
            onClosePageOptions={() => {
              setSelected(undefined);
            }}
          />
        ))}
      </div>
      <div className="mt-3 flex justify-end pb-2">
        {!reviewed && (
          <Button
            size="dense"
            variant="secondary"
            disabled={review.isPending}
            onClick={() =>
              review.mutate({
                op: "confirm_layout",
                layout_version: data.layout?.version ?? 0,
              })
            }
          >
            ✓ Confirm
          </Button>
        )}
      </div>
    </ReviewSection>
  );
}
